import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { DailyNoteRecord, EventRecord, TaskRecord } from '../api';
import { formatDisplayDate, formatDateKey } from '../utils/date';

interface Props {
  tasks: TaskRecord[];
  events: EventRecord[];
  notes: DailyNoteRecord[];
  onClose: () => void;
  onSelectTask: (task: TaskRecord) => void;
  onSelectEvent: (event: EventRecord) => void;
  onOpenNote: (date: string) => void;
  onAddTask: (title: string) => void;
  onCaptureNote: (text: string) => void;
  onAddEventStub: (title: string) => void;
  onNavigate: (action: string) => void;
}

type CommandItem = {
  id: string;
  icon: string;
  label: string;
  hint?: string;
  action: () => void;
};

export function CommandBar({
  tasks,
  events,
  notes,
  onClose,
  onSelectTask,
  onSelectEvent,
  onOpenNote,
  onAddTask,
  onCaptureNote,
  onAddEventStub,
  onNavigate,
}: Props) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const commands: CommandItem[] = [
      { id: 'nav-today', icon: '📍', label: 'Open Today view', hint: 'Navigate', action: () => onNavigate('view-today') },
      { id: 'nav-calendar', icon: '🗓️', label: 'Open Calendar view', hint: 'Navigate', action: () => onNavigate('view-calendar') },
      { id: 'nav-notes', icon: '📝', label: 'Open Notes view', hint: 'Navigate', action: () => onNavigate('view-notes') },
      { id: 'nav-review', icon: '✅', label: 'Open Review view', hint: 'Navigate', action: () => onNavigate('view-review') },
      { id: 'filter-pending', icon: '⏳', label: 'Show pending tasks', hint: 'Filter', action: () => onNavigate('filter-pending') },
      { id: 'filter-all', icon: '📋', label: 'Show all tasks', hint: 'Filter', action: () => onNavigate('filter-all') },
    ];

    const normalizedQuery = query.trim().toLowerCase();

    if (normalizedQuery) {
      commands.unshift(
        {
          id: 'capture-task',
          icon: '➕',
          label: `Capture inbox task: "${query}"`,
          hint: 'Task',
          action: () => onAddTask(query.trim()),
        },
        {
          id: 'capture-note',
          icon: '📝',
          label: `Log to today's note: "${query}"`,
          hint: formatDateKey(new Date()),
          action: () => onCaptureNote(query.trim()),
        },
        {
          id: 'capture-event',
          icon: '🗓️',
          label: `Create event stub: "${query}"`,
          hint: 'Today',
          action: () => onAddEventStub(query.trim()),
        },
      );
    }

    const taskMatches = tasks
      .filter((task) => matches(task.title, normalizedQuery) || matches(task.notes || '', normalizedQuery))
      .slice(0, 6)
      .map<CommandItem>((task) => ({
        id: `task-${task.id}`,
        icon: task.status === 'done' ? '✓' : task.status === 'blocked' ? '🚫' : '○',
        label: task.title,
        hint: task.notes ? 'Task note match' : task.categoryName || 'Task',
        action: () => onSelectTask(task),
      }));

    const eventMatches = events
      .filter((event) => matches(event.title, normalizedQuery) || matches(event.description || '', normalizedQuery))
      .slice(0, 5)
      .map<CommandItem>((event) => ({
        id: `event-${event.id}`,
        icon: '🗓️',
        label: event.title,
        hint: event.description ? 'Event note match' : formatDisplayDate(event.eventDate, { month: 'short', day: 'numeric' }),
        action: () => onSelectEvent(event),
      }));

    const noteMatches = notes
      .filter((note) => matches(note.title || '', normalizedQuery) || matches(note.content, normalizedQuery))
      .slice(0, 5)
      .map<CommandItem>((note) => ({
        id: `note-${note.id}`,
        icon: '📝',
        label: note.title || formatDisplayDate(note.noteDate, { month: 'short', day: 'numeric' }),
        hint: formatDisplayDate(note.noteDate, { weekday: 'short', month: 'short', day: 'numeric' }),
        action: () => onOpenNote(note.noteDate),
      }));

    const combined = [...commands, ...taskMatches, ...eventMatches, ...noteMatches];

    if (!normalizedQuery) {
      return combined;
    }

    return combined.filter((command) =>
      matches(command.label, normalizedQuery) ||
      matches(command.hint || '', normalizedQuery),
    );
  }, [events, notes, onAddEventStub, onAddTask, onCaptureNote, onNavigate, onOpenNote, onSelectEvent, onSelectTask, query, tasks]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === 'Enter' && filtered[activeIndex]) {
      event.preventDefault();
      filtered[activeIndex].action();
      return;
    }

    if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="command-overlay" onClick={onClose}>
      <div className="command-bar" onClick={(event) => event.stopPropagation()}>
        <input
          ref={inputRef}
          className="command-input"
          type="text"
          placeholder="Capture a task, note, event, or jump to a view..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className="command-results">
          {filtered.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No results. Try capturing this as a task or note.
            </div>
          ) : (
            filtered.map((item, index) => (
              <div
                key={item.id}
                className={`command-item ${index === activeIndex ? 'active' : ''}`}
                onClick={item.action}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <div className="icon">{item.icon}</div>
                <div className="label">{item.label}</div>
                {item.hint && <div className="hint">{item.hint}</div>}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function matches(value: string, query: string): boolean {
  if (!query) {
    return true;
  }

  return value.toLowerCase().includes(query);
}
